import { ANNOUNCEMENT_SWATCHES } from '@accounts/shared';
import type {
  AnnouncementPublic,
  AnnouncementScheduleState,
  AnnouncementSwatch,
  AnnouncementTypePublic,
  ApplicationPublic,
  CreateAnnouncementRequest,
  UpdateAnnouncementRequest,
} from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { STR } from '../../i18n';
import {
  ApiError,
  createAnnouncement,
  createAnnouncementType,
  deactivateAnnouncement,
  listAnnouncementTypes,
  listAnnouncements,
  listApplications,
  reactivateAnnouncement,
  retireAnnouncementType,
  updateAnnouncement,
  updateAnnouncementType,
} from '../../lib/api';
import { formatDate, formatDateTime, isoToLocalInput, localInputToIso } from '../../lib/format';
import { queryKeys } from '../../lib/queryKeys';
import { showToast } from '../../lib/toast';
import { Modal, ModalForm, ModalParagraph, ModalProse } from '../../ui/Modal';
import {
  Callout,
  Chip,
  type ChipTone,
  EmptyScreen,
  Skeleton,
  SwatchStrip,
} from '../../ui/Primitives';
import {
  cardStyle,
  checkboxRowStyle,
  checkboxStyle,
  detailPaneStyle,
  eyebrowStyle,
  fieldLabelStyle,
  hintStyle,
  modalButtonStyle,
  quietButtonStyle,
  rowActionStyle,
  textFieldStyle,
} from '../../ui/styles';

import { AdminShell } from './AdminShell';

/**
 * ANNOUNCEMENTS — notices posted to everyone's launcher, administered here, plus the
 * managed TYPE vocabulary behind their colour-coded pills.
 *
 * THE LIST IS A FULL-WIDTH TABLE (the design of record's variation D, ruled by the
 * operator; the card-grid variation C is not part of this product and no code for it
 * exists here). Its columns are Announcement / Schedule / Application / Type / Window /
 * Actions, and an untyped or untargeted announcement leaves that CELL EMPTY — an empty
 * cell is the correct rendering, not a dash and not the word "none".
 *
 * SCHEDULE STATE HAS EXACTLY ONE HOME, AND IT IS THE SERVER. Every row's state arrives as
 * `scheduleState` and is rendered verbatim; nothing in this file (or anywhere in /web)
 * compares `startsAt`/`endsAt` to the clock to decide what is live. The Window column
 * PRESENTS those two timestamps, which is a different thing entirely.
 *
 * RETIRE, NEVER DELETE (D5). There is no delete affordance on either tab because there is
 * no delete endpoint. An announcement leaves the launcher by deactivation and stays listed
 * here; a type is retired and keeps rendering forever on the announcements carrying it.
 *
 * Both lists are unpaginated by contract — the sets are inherently small — so the header
 * counts are honestly derived from the rows in hand: they ARE the whole collection.
 */

/** The server's own bounds, mirrored as affordance; its refusal renders if one arrives. */
const BODY_MAX = 2000;
const TITLE_MAX = 120;
/** The applications list feeds the target selector only — the same cap the other screens use. */
const LIST_LIMIT = 200;

type Tab = 'list' | 'types';

type ModalState =
  | { kind: 'compose'; announcement: AnnouncementPublic | null }
  | { kind: 'deactivate'; announcement: AnnouncementPublic }
  | { kind: 'reactivateEnded'; announcement: AnnouncementPublic; message: string }
  | { kind: 'typeForm'; type: AnnouncementTypePublic | null }
  | { kind: 'retireType'; type: AnnouncementTypePublic; usedOn: number }
  | null;

/**
 * The Schedule pill, straight off the server's `scheduleState` — plus the one state that is
 * NOT a schedule state at all. A manually deactivated announcement is off the launcher
 * whatever its window says, so it wears its own neutral label instead of a schedule word
 * that would be true of the schedule and false of the announcement.
 */
function schedulePillOf(announcement: AnnouncementPublic): { tone: ChipTone; label: string } {
  if (!announcement.active) return { tone: 'neutral', label: STR.announcements.scheduleOff };
  const states: Record<AnnouncementScheduleState, { tone: ChipTone; label: string }> = {
    scheduled: { tone: 'attention', label: STR.announcements.scheduleScheduled },
    live: { tone: 'positive', label: STR.announcements.scheduleLive },
    // Soft red (`c-rose`), never the strong danger red: an ended announcement ran its
    // course, which is not a fault.
    ended: { tone: 'expired', label: STR.announcements.scheduleEnded },
  };
  return states[announcement.scheduleState];
}

/** PRESENTS the two timestamps. Not a state derivation — the state is the server's. */
function windowLineOf(announcement: AnnouncementPublic): string {
  const parts: string[] = [];
  if (announcement.startsAt !== null) {
    parts.push(STR.announcements.windowStarts(formatDateTime(announcement.startsAt)));
  }
  if (announcement.endsAt !== null) {
    parts.push(STR.announcements.windowEnds(formatDateTime(announcement.endsAt)));
  }
  return parts.length === 0 ? STR.announcements.windowAlways : parts.join(' · ');
}

/** Who wrote it and when — by ID join. A null author is stated, never given a stand-in name. */
function postedLineOf(announcement: AnnouncementPublic): string {
  const at = formatDate(announcement.createdAt);
  return announcement.author === null
    ? STR.announcements.postedLineNoAuthor(at)
    : STR.announcements.postedLine(at, announcement.author.name);
}

export function AnnouncementsPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('list');
  const [modal, setModal] = useState<ModalState>(null);

  const announcementsQuery = useQuery({
    queryKey: queryKeys.announcements,
    queryFn: listAnnouncements,
  });
  const typesQuery = useQuery({
    queryKey: queryKeys.announcementTypes,
    queryFn: listAnnouncementTypes,
  });
  const applicationsQuery = useQuery({
    queryKey: queryKeys.applications,
    queryFn: () => listApplications({ limit: LIST_LIMIT }),
  });

  const rows = useMemo(() => announcementsQuery.data?.data ?? [], [announcementsQuery.data]);
  const types = useMemo(() => typesQuery.data?.data ?? [], [typesQuery.data]);
  const applications = applicationsQuery.data?.data ?? [];

  /*
   * THE COLOUR RIDES THE ROW (U2). An announcement's joined application carries the
   * application's own pill colour (`application.color`), resolved by the SAME id join that
   * supplies its name — so there is no colour map here and no second read to keep in step.
   * Null = no colour assigned = a neutral chip, never a guessed default.
   */

  /**
   * How many announcements carry each type — counted off the loaded list, which IS the
   * whole collection (this endpoint is unpaginated), so the number is honest. Keyed by id.
   */
  const typeUsage = useMemo(() => {
    const counts = new Map<string, number>();
    for (const announcement of rows) {
      if (announcement.typeId === null) continue;
      counts.set(announcement.typeId, (counts.get(announcement.typeId) ?? 0) + 1);
    }
    return counts;
  }, [rows]);

  const counts = { live: 0, scheduled: 0, ended: 0, off: 0 };
  for (const announcement of rows) {
    if (!announcement.active) counts.off += 1;
    else counts[announcement.scheduleState] += 1;
  }

  function invalidateAnnouncements() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.announcements });
    // The launcher's own list is a different endpoint under the same client — keep it honest.
    void queryClient.invalidateQueries({ queryKey: queryKeys.myAnnouncements });
  }

  function invalidateTypes() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.announcementTypes });
    // A type's name and colour are joined onto every announcement carrying it, on both
    // surfaces — so a rename or recolour changes what those two reads return.
    invalidateAnnouncements();
  }

  const deactivateMutation = useMutation({
    mutationFn: deactivateAnnouncement,
    onSuccess: () => {
      invalidateAnnouncements();
      setModal(null);
      showToast(STR.toast.announcementDeactivated, 'warn');
    },
    onError: (failure) => {
      setModal(null);
      showToast(failure instanceof ApiError ? failure.message : STR.errors.generic, 'warn');
    },
  });

  /*
   * Reactivating is direct — one takes a notice off everyone's screen, the other only puts
   * one back — with ONE refusal to surface: an announcement whose end time is already past
   * would "succeed" into permanent invisibility, so the SERVER refuses it (400). This screen
   * does not pre-empt that by reading the timestamps itself; it asks, and renders the
   * server's own sentence in a dialog that offers the only thing which fixes it.
   */
  const reactivateMutation = useMutation({
    mutationFn: (announcement: AnnouncementPublic) => reactivateAnnouncement(announcement.id),
    onSuccess: () => {
      invalidateAnnouncements();
      showToast(STR.toast.announcementReactivated);
    },
    onError: (failure, announcement) => {
      if (failure instanceof ApiError && failure.status === 400) {
        setModal({ kind: 'reactivateEnded', announcement, message: failure.message });
        return;
      }
      showToast(failure instanceof ApiError ? failure.message : STR.errors.generic, 'warn');
    },
  });

  const retireTypeMutation = useMutation({
    mutationFn: (type: AnnouncementTypePublic) => retireAnnouncementType(type.id),
    onSuccess: (_result, type) => {
      invalidateTypes();
      setModal(null);
      showToast(STR.toast.typeRetired(type.name), 'warn');
    },
    onError: (failure) => {
      setModal(null);
      showToast(failure instanceof ApiError ? failure.message : STR.errors.generic, 'warn');
    },
  });

  const listLoading = announcementsQuery.isLoading;
  const typesLoading = typesQuery.isLoading;

  return (
    <AdminShell
      surfaceId="announcements"
      primary={{
        label:
          tab === 'types' ? STR.announcements.types.newType : STR.surfaces.announcements.primary,
        onClick: () => {
          setModal(tab === 'types' ? { kind: 'typeForm', type: null } : { kind: 'compose', announcement: null });
        },
      }}
    >
      <div style={detailPaneStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div className="tgl" role="group" aria-label={STR.announcements.tabsLabel}>
            <button
              type="button"
              className="seg"
              aria-pressed={tab === 'list'}
              style={{ padding: '6px 13px', borderRadius: 6, fontSize: 12.5, fontWeight: 650, cursor: 'pointer' }}
              onClick={() => {
                setTab('list');
              }}
            >
              {STR.announcements.tabList}
            </button>
            <button
              type="button"
              className="seg"
              aria-pressed={tab === 'types'}
              style={{ padding: '6px 13px', borderRadius: 6, fontSize: 12.5, fontWeight: 650, cursor: 'pointer' }}
              onClick={() => {
                setTab('types');
              }}
            >
              {STR.announcements.tabTypes}
            </button>
          </div>
        </div>

        {tab === 'list' ? (
          <>
            {listLoading ? <ListSkeleton /> : null}

            {/* A FAILED READ AND AN EMPTY LIST MUST NOT LOOK THE SAME. */}
            {!listLoading && announcementsQuery.isError ? (
              <ReadFailed
                onRetry={() => {
                  void announcementsQuery.refetch();
                }}
              />
            ) : null}

            {!listLoading && !announcementsQuery.isError && rows.length === 0 ? (
              <EmptyScreen
                title={STR.announcements.emptyTitle}
                body={STR.announcements.emptyBody}
                action={
                  <button
                    type="button"
                    className="prim"
                    style={modalButtonStyle}
                    onClick={() => {
                      setModal({ kind: 'compose', announcement: null });
                    }}
                  >
                    {STR.surfaces.announcements.primary}
                  </button>
                }
              />
            ) : null}

            {!listLoading && !announcementsQuery.isError && rows.length > 0 ? (
              <div style={{ ...cardStyle, width: '100%' }}>
                <div
                  className="tx-subtle"
                  style={{
                    ...eyebrowStyle,
                    padding: '12px 18px',
                    borderBottom: '1px solid var(--border)',
                    background: 'var(--surface-2)',
                  }}
                >
                  {STR.announcements.meta(counts.live, counts.scheduled, counts.ended, counts.off)}
                </div>
                <div className="dscroll">
                  <table className="dtable">
                    <thead>
                      <tr>
                        <th scope="col">{STR.announcements.columns.announcement}</th>
                        <th scope="col">{STR.announcements.columns.schedule}</th>
                        <th scope="col">{STR.announcements.columns.application}</th>
                        <th scope="col">{STR.announcements.columns.type}</th>
                        <th scope="col">{STR.announcements.columns.window}</th>
                        <th scope="col" className="right">
                          {STR.announcements.columns.actions}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((announcement) => {
                        const pill = schedulePillOf(announcement);
                        return (
                          <tr
                            key={announcement.id}
                            className="row"
                            {...(announcement.active ? {} : { 'data-off': true })}
                          >
                            <td style={{ maxWidth: 340 }}>
                              <div className="nm nm-tr" style={{ fontSize: 13, fontWeight: 600 }}>
                                {announcement.title ?? STR.announcements.untitled}
                              </div>
                              <div className="tx-muted" style={{ fontSize: 11, marginTop: 2 }}>
                                {postedLineOf(announcement)}
                              </div>
                            </td>
                            <td>
                              <Chip tone={pill.tone} style={{ fontSize: 10.5, padding: '2px 8px' }}>
                                {pill.label}
                              </Chip>
                            </td>
                            {/* AN UNTARGETED ANNOUNCEMENT LEAVES THIS CELL EMPTY. */}
                            <td>
                              {announcement.application === null ? null : (
                                <Chip
                                  tone="neutral"
                                  swatch={announcement.application.color}
                                  style={{ fontSize: 10.5, padding: '2px 8px' }}
                                >
                                  <span className="ptr">{announcement.application.name}</span>
                                </Chip>
                              )}
                            </td>
                            {/* AN UNTYPED ANNOUNCEMENT LEAVES THIS CELL EMPTY. */}
                            <td>
                              {announcement.type === null ? null : (
                                <Chip
                                  tone="neutral"
                                  swatch={announcement.type.color}
                                  style={{ fontSize: 10.5, padding: '2px 8px' }}
                                >
                                  <span className="ptr">{announcement.type.name}</span>
                                </Chip>
                              )}
                            </td>
                            <td className="tx-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                              {windowLineOf(announcement)}
                            </td>
                            <td className="right">
                              <button
                                type="button"
                                className="tbtn"
                                style={rowActionStyle}
                                onClick={() => {
                                  setModal({ kind: 'compose', announcement });
                                }}
                              >
                                {STR.announcements.edit}
                              </button>
                              {announcement.active ? (
                                <button
                                  type="button"
                                  className="tbtn tbtn-danger"
                                  style={rowActionStyle}
                                  onClick={() => {
                                    setModal({ kind: 'deactivate', announcement });
                                  }}
                                >
                                  {STR.announcements.deactivate}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="tbtn"
                                  style={rowActionStyle}
                                  disabled={reactivateMutation.isPending}
                                  onClick={() => {
                                    reactivateMutation.mutate(announcement);
                                  }}
                                >
                                  {STR.announcements.reactivate}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <>
            {typesLoading ? <ListSkeleton /> : null}

            {!typesLoading && typesQuery.isError ? (
              <ReadFailed
                onRetry={() => {
                  void typesQuery.refetch();
                }}
              />
            ) : null}

            {!typesLoading && !typesQuery.isError && types.length === 0 ? (
              <EmptyScreen
                title={STR.announcements.types.emptyTitle}
                body={STR.announcements.types.emptyBody}
                action={
                  <button
                    type="button"
                    className="prim"
                    style={modalButtonStyle}
                    onClick={() => {
                      setModal({ kind: 'typeForm', type: null });
                    }}
                  >
                    {STR.announcements.types.newType}
                  </button>
                }
              />
            ) : null}

            {!typesLoading && !typesQuery.isError && types.length > 0 ? (
              <TypesTable
                types={types}
                usage={typeUsage}
                onEdit={(type) => {
                  setModal({ kind: 'typeForm', type });
                }}
                onRetire={(type) => {
                  setModal({ kind: 'retireType', type, usedOn: typeUsage.get(type.id) ?? 0 });
                }}
              />
            ) : null}
          </>
        )}
      </div>

      {modal?.kind === 'compose' ? (
        <AnnouncementModal
          announcement={modal.announcement}
          applications={applications}
          types={types}
          onClose={() => {
            setModal(null);
          }}
        />
      ) : null}

      {modal?.kind === 'deactivate' ? (
        <Modal
          title={STR.modals.deactivateAnnouncement.title}
          onClose={() => {
            setModal(null);
          }}
          footer={
            <>
              <button
                type="button"
                className="ghost"
                style={modalButtonStyle}
                onClick={() => {
                  setModal(null);
                }}
              >
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="dbtn"
                style={modalButtonStyle}
                disabled={deactivateMutation.isPending}
                onClick={() => {
                  deactivateMutation.mutate(modal.announcement.id);
                }}
              >
                {STR.modals.deactivateAnnouncement.submit}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>{STR.modals.deactivateAnnouncement.body}</ModalParagraph>
          </ModalProse>
        </Modal>
      ) : null}

      {modal?.kind === 'reactivateEnded' ? (
        <Modal
          title={STR.modals.reactivateEnded.title}
          onClose={() => {
            setModal(null);
          }}
          footer={
            <>
              <button
                type="button"
                className="ghost"
                style={modalButtonStyle}
                onClick={() => {
                  setModal(null);
                }}
              >
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="prim"
                style={modalButtonStyle}
                onClick={() => {
                  setModal({ kind: 'compose', announcement: modal.announcement });
                }}
              >
                {STR.modals.reactivateEnded.submit}
              </button>
            </>
          }
        >
          <ModalProse>
            {/* The SERVER's own sentence, verbatim — it names the fact this dialog exists
                for, and it is written for administrators. */}
            <Callout tone="danger" role="alert" style={{ fontSize: 13, lineHeight: 1.6 }}>
              <strong style={{ fontWeight: 700 }}>{modal.message}</strong>{' '}
              {STR.modals.reactivateEnded.body}
            </Callout>
            <p className="tx-muted" style={{ margin: '14px 0 0', fontSize: 13, lineHeight: 1.6 }}>
              {STR.modals.reactivateEnded.hint}
            </p>
          </ModalProse>
        </Modal>
      ) : null}

      {modal?.kind === 'typeForm' ? (
        <TypeFormModal
          type={modal.type}
          onClose={() => {
            setModal(null);
          }}
        />
      ) : null}

      {modal?.kind === 'retireType' ? (
        <Modal
          title={STR.modals.retireAnnouncementType.title(modal.type.name)}
          onClose={() => {
            setModal(null);
          }}
          footer={
            <>
              <button
                type="button"
                className="ghost"
                style={modalButtonStyle}
                onClick={() => {
                  setModal(null);
                }}
              >
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="dbtn"
                style={modalButtonStyle}
                disabled={retireTypeMutation.isPending}
                onClick={() => {
                  retireTypeMutation.mutate(modal.type);
                }}
              >
                {STR.modals.retireAnnouncementType.submit}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>
              {STR.modals.retireAnnouncementType.body(modal.usedOn)}
            </ModalParagraph>
            <p className="tx-subtle" style={{ margin: '10px 0 0', fontSize: 12.5, lineHeight: 1.6 }}>
              {STR.modals.retireAnnouncementType.permanent}
            </p>
          </ModalProse>
        </Modal>
      ) : null}
    </AdminShell>
  );
}

function ReadFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <Callout
      tone="danger"
      role="alert"
      style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
    >
      <span style={{ flex: 1, minWidth: 180 }}>{STR.errors.couldNotLoad}</span>
      <button type="button" className="tbtn" style={quietButtonStyle} onClick={onRetry}>
        {STR.common.tryAgain}
      </button>
    </Callout>
  );
}

/** The screen's own first paint — the shape of the rows that are coming. */
function ListSkeleton() {
  return (
    <div style={{ ...cardStyle, width: '100%' }} aria-hidden="true">
      {[0, 1, 2].map((row) => (
        <div
          key={row}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <Skeleton style={{ height: 12, width: 190, borderRadius: 5 }} />
          <Skeleton style={{ height: 9, width: 70, borderRadius: 5 }} />
          <Skeleton style={{ height: 9, width: 90, borderRadius: 5 }} />
          <Skeleton style={{ height: 9, width: 130, borderRadius: 5, marginLeft: 'auto' }} />
        </div>
      ))}
    </div>
  );
}

/**
 * THE TYPE MANAGER. Same table anatomy as the announcement list, plus the Colour column —
 * the one column the launcher-tag manager (Settings) deliberately does not have, because
 * tags are neutral by ruling.
 *
 * A RETIRED type carries the panel's existing deactivated-row treatment and offers NO
 * actions: there is no restore endpoint and editing one is refused server-side, because
 * its name and colour are already rendering on historical announcements exactly as they
 * were retired. The row says so rather than showing a control that would 400.
 */
function TypesTable({
  types,
  usage,
  onEdit,
  onRetire,
}: {
  types: AnnouncementTypePublic[];
  usage: Map<string, number>;
  onEdit: (type: AnnouncementTypePublic) => void;
  onRetire: (type: AnnouncementTypePublic) => void;
}) {
  const active = types.filter((type) => type.retiredAt === null).length;

  return (
    <div style={{ ...cardStyle, width: '100%' }}>
      <div
        className="tx-subtle"
        style={{
          ...eyebrowStyle,
          padding: '12px 18px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface-2)',
        }}
      >
        {STR.announcements.types.meta(active, types.length - active)}
      </div>
      <div className="dscroll">
        <table className="dtable">
          <thead>
            <tr>
              <th scope="col">{STR.announcements.types.columns.type}</th>
              <th scope="col">{STR.announcements.types.columns.colour}</th>
              <th scope="col">{STR.announcements.types.columns.usedOn}</th>
              <th scope="col">{STR.announcements.types.columns.state}</th>
              <th scope="col" className="right">
                {STR.announcements.types.columns.actions}
              </th>
            </tr>
          </thead>
          <tbody>
            {types.map((type) => {
              const retired = type.retiredAt !== null;
              return (
                <tr key={type.id} className="row" {...(retired ? { 'data-off': true } : {})}>
                  <td style={{ maxWidth: 280 }}>
                    <Chip
                      tone="neutral"
                      swatch={type.color}
                      style={{ fontSize: 10.5, padding: '2px 8px' }}
                    >
                      <span className="ptr">{type.name}</span>
                    </Chip>
                  </td>
                  <td className="tx-muted" style={{ fontSize: 12 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                      <span
                        className={`swx c-${type.color}`}
                        style={{ width: 16, height: 16, borderRadius: 5, cursor: 'default' }}
                        aria-hidden="true"
                      />
                      {STR.swatchNames[type.color]}
                    </span>
                  </td>
                  <td
                    className="tx-muted"
                    style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {STR.announcements.types.usedOnCount(usage.get(type.id) ?? 0)}
                  </td>
                  <td>
                    <Chip tone={retired ? 'neutral' : 'positive'} size="sm">
                      {retired
                        ? STR.announcements.types.stateRetired
                        : STR.announcements.types.stateActive}
                    </Chip>
                  </td>
                  <td className="right">
                    {retired ? (
                      <span className="tx-subtle" style={{ fontSize: 11.5 }}>
                        {STR.announcements.types.retiredNote}
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="tbtn"
                          style={rowActionStyle}
                          onClick={() => {
                            onEdit(type);
                          }}
                        >
                          {STR.announcements.types.edit}
                        </button>
                        <button
                          type="button"
                          className="tbtn tbtn-danger"
                          style={rowActionStyle}
                          onClick={() => {
                            onRetire(type);
                          }}
                        >
                          {STR.announcements.types.retire}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * ONE dialog for posting and editing — same fields, different verbs.
 *
 * `createdBy` is NEVER sent: authorship is the acting session, resolved server-side. The
 * edit path sends a TRUE PATCH — only the fields that actually changed — which is not
 * tidiness but correctness: the server refuses a (re)assignment of an inactive application
 * or a retired type, and re-sending an untouched one would turn "I edited the body" into a
 * 400 on an announcement that is perfectly legal as it stands.
 *
 * THE PREVIEW renders the launcher card. Its body is the SERVER's `bodyHtml` whenever the
 * draft still matches what is saved; the moment the text differs there is no rendered HTML
 * to show — this app has no client-side markdown renderer, by ruling — so the preview shows
 * the text exactly as typed, in the same whitespace-preserving container, and says so.
 */
function AnnouncementModal({
  announcement,
  applications,
  types,
  onClose,
}: {
  announcement: AnnouncementPublic | null;
  applications: ApplicationPublic[];
  types: AnnouncementTypePublic[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(announcement?.title ?? '');
  const [body, setBody] = useState(announcement?.body ?? '');
  const [applicationId, setApplicationId] = useState(announcement?.applicationId ?? '');
  const [showEveryone, setShowEveryone] = useState(announcement?.showEveryone ?? false);
  const [typeId, setTypeId] = useState(announcement?.typeId ?? '');
  const [starts, setStarts] = useState(isoToLocalInput(announcement?.startsAt));
  const [ends, setEnds] = useState(isoToLocalInput(announcement?.endsAt));
  const [error, setError] = useState<string | null>(null);

  /*
   * The target selector offers ACTIVE applications — the server refuses targeting an
   * inactive one. An announcement already pointing at an application that has since been
   * deactivated keeps its option so the select tells the truth about what it holds; that
   * assignment is never re-sent unless the operator changes it.
   */
  const applicationOptions = applications.filter(
    (application) => application.active || application.id === announcement?.applicationId,
  );
  /*
   * Live types only — a RETIRED type is refused for a new or changed assignment. Same
   * exception, same reason: a retired type already carried stays visible in its own select.
   */
  const liveTypes = types.filter((type) => type.retiredAt === null);
  const carriedRetiredType =
    announcement?.type !== undefined &&
    announcement?.type !== null &&
    announcement.type.retiredAt !== null
      ? announcement.type
      : null;

  const saveMutation = useMutation({
    mutationFn: () => {
      const trimmedTitle = title.trim();
      const trimmedBody = body.trim();
      if (announcement === null) {
        // typeId is REQUIRED (D101) — submit() has already refused an empty one, so it is
        // set directly in the initializer rather than conditionally assigned after.
        const payload: CreateAnnouncementRequest = { body: trimmedBody, typeId };
        // Empty title means "not set" — omitted rather than sent as ''.
        if (trimmedTitle !== '') payload.title = trimmedTitle;
        if (applicationId !== '') {
          payload.applicationId = applicationId;
          payload.showEveryone = showEveryone;
        }
        if (starts !== '') payload.startsAt = localInputToIso(starts);
        if (ends !== '') payload.endsAt = localInputToIso(ends);
        return createAnnouncement(payload);
      }

      const patch: UpdateAnnouncementRequest = {};
      // On PATCH an empty-string title CLEARS the field (stores null) — sent on purpose.
      if (trimmedTitle !== (announcement.title ?? '')) patch.title = trimmedTitle;
      if (trimmedBody !== announcement.body) patch.body = trimmedBody;
      const nextApplicationId = applicationId === '' ? null : applicationId;
      if (nextApplicationId !== announcement.applicationId) patch.applicationId = nextApplicationId;
      if (showEveryone !== announcement.showEveryone) patch.showEveryone = showEveryone;
      // typeId can never be sent as null (D101: clearing a type is refused) — sent only when
      // it actually changed. An announcement carrying a RETIRED type, edited without
      // touching its type, must not re-send it: typeId still equals announcement.typeId, so
      // this condition is false and the carried type passes through untouched.
      if (typeId !== '' && typeId !== announcement.typeId) patch.typeId = typeId;
      const nextStarts = localInputToIso(starts);
      if (starts !== isoToLocalInput(announcement.startsAt)) patch.startsAt = nextStarts;
      const nextEnds = localInputToIso(ends);
      if (ends !== isoToLocalInput(announcement.endsAt)) patch.endsAt = nextEnds;
      return updateAnnouncement(announcement.id, patch);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.announcements });
      void queryClient.invalidateQueries({ queryKey: queryKeys.myAnnouncements });
      showToast(
        announcement === null ? STR.toast.announcementPosted : STR.toast.announcementUpdated,
      );
      onClose();
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  function submit() {
    const trimmedBody = body.trim();
    if (trimmedBody === '') {
      setError(STR.modals.announcement.bodyRequired);
      return;
    }
    // Affordance only — the server refuses an untyped announcement independently (D101).
    if (typeId === '') {
      setError(STR.modals.announcement.typeRequired);
      return;
    }
    if (trimmedBody.length > BODY_MAX) {
      setError(STR.modals.announcement.bodyTooLong);
      return;
    }
    if (starts !== '' && localInputToIso(starts) === null) {
      setError(STR.modals.announcement.scheduleInvalid);
      return;
    }
    if (ends !== '' && localInputToIso(ends) === null) {
      setError(STR.modals.announcement.scheduleInvalid);
      return;
    }
    // Affordance only — the server enforces the half-open window independently.
    const startIso = localInputToIso(starts);
    const endIso = localInputToIso(ends);
    if (startIso !== null && endIso !== null && startIso >= endIso) {
      setError(STR.modals.announcement.scheduleBackwards);
      return;
    }
    saveMutation.mutate();
  }

  const overBudget = body.length > BODY_MAX;
  const previewTitle = title.trim();
  // Editing previews the announcement's real date; posting previews today's, as it will post.
  const previewDate = formatDate(announcement?.createdAt ?? new Date().toISOString());
  const previewApplication = applicationOptions.find(
    (application) => application.id === applicationId,
  );
  const previewType =
    typeId === ''
      ? null
      : (liveTypes.find((type) => type.id === typeId) ??
        (carriedRetiredType?.id === typeId ? carriedRetiredType : null));
  /** The server's rendered HTML is only honest while the draft still matches what it rendered. */
  const savedHtml =
    announcement !== null && body === announcement.body ? announcement.bodyHtml : null;

  return (
    <Modal
      title={
        announcement === null
          ? STR.modals.announcement.titlePost
          : STR.modals.announcement.titleEdit
      }
      lead={STR.modals.announcement.lead}
      width="min(920px,100%)"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onClose}>
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            // Zero live types and nothing carried forward means saving cannot succeed
            // (D101 requires a type) — disabled rather than inviting a guaranteed refusal.
            disabled={
              saveMutation.isPending || (liveTypes.length === 0 && carriedRetiredType === null)
            }
            onClick={submit}
          >
            {announcement === null
              ? STR.modals.announcement.submitPost
              : STR.modals.announcement.submitEdit}
          </button>
        </>
      }
    >
      <div
        style={{
          padding: '18px 22px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
          gap: 20,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          {error === null ? null : (
            <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
              {error}
            </Callout>
          )}

          <label style={{ display: 'block' }}>
            <span
              className="tx-muted"
              style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'baseline', gap: 6 }}
            >
              {STR.modals.announcement.titleLabel}{' '}
              <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 500 }}>
                {STR.settings.optional}
              </span>
            </span>
            <input
              className="field"
              type="text"
              maxLength={TITLE_MAX}
              value={title}
              placeholder={STR.modals.announcement.titlePlaceholder}
              onChange={(event) => {
                setTitle(event.target.value);
                setError(null);
              }}
              style={textFieldStyle}
            />
          </label>

          <label style={{ display: 'block' }}>
            <span
              className="tx-muted"
              style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'baseline' }}
            >
              {STR.modals.announcement.bodyLabel}
              <span
                className={overBudget ? 'tx-warn-strong' : 'tx-subtle'}
                style={{
                  marginLeft: 'auto',
                  fontSize: 11,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {STR.modals.announcement.count(body.length)}
              </span>
            </span>
            <textarea
              className={overBudget ? 'field field-over' : 'field'}
              rows={7}
              value={body}
              placeholder={STR.modals.announcement.bodyPlaceholder}
              onChange={(event) => {
                setBody(event.target.value);
                setError(null);
              }}
              style={{ ...textFieldStyle, lineHeight: 1.5, resize: 'vertical', minHeight: 120 }}
            />
            <span className="tx-subtle" style={hintStyle}>
              {STR.modals.announcement.bodyHint}
            </span>
          </label>

          <label style={{ display: 'block' }}>
            <span className="tx-muted" style={fieldLabelStyle}>
              {STR.modals.announcement.applicationLabel}
            </span>
            <select
              className="field"
              value={applicationId}
              onChange={(event) => {
                setApplicationId(event.target.value);
                // Untargeted already reaches everyone, so the widening flag is meaningless
                // without a target — cleared here rather than sent as a stale true.
                if (event.target.value === '') setShowEveryone(false);
                setError(null);
              }}
              style={{ ...textFieldStyle, padding: '9px 30px 9px 11px' }}
            >
              <option value="">{STR.modals.announcement.applicationNone}</option>
              {applicationOptions.map((application) => (
                <option key={application.id} value={application.id}>
                  {application.name}
                </option>
              ))}
            </select>
          </label>

          {applicationId === '' ? (
            <div
              style={{
                display: 'flex',
                gap: 11,
                alignItems: 'flex-start',
                padding: '12px 13px',
                borderRadius: 9,
                border: '1px dashed var(--border)',
              }}
            >
              <input type="checkbox" disabled checked={false} readOnly style={checkboxStyle} />
              <span>
                <span
                  className="tx-subtle"
                  style={{ display: 'block', fontSize: 13, fontWeight: 650 }}
                >
                  {STR.modals.announcement.everyoneTitle}
                </span>
                <span
                  className="tx-subtle"
                  style={{ display: 'block', fontSize: 12, marginTop: 2, lineHeight: 1.5 }}
                >
                  {STR.modals.announcement.everyoneDisabledNote}
                </span>
              </span>
            </div>
          ) : (
            <label style={checkboxRowStyle}>
              <input
                type="checkbox"
                checked={showEveryone}
                onChange={(event) => {
                  setShowEveryone(event.target.checked);
                  setError(null);
                }}
                style={checkboxStyle}
              />
              <span>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 650 }}>
                  {STR.modals.announcement.everyoneTitle}
                </span>
                <span
                  className="tx-subtle"
                  style={{ display: 'block', fontSize: 12, marginTop: 2, lineHeight: 1.5 }}
                >
                  {STR.modals.announcement.everyoneNote}
                </span>
              </span>
            </label>
          )}

          <label style={{ display: 'block' }}>
            <span className="tx-muted" style={fieldLabelStyle}>
              {STR.modals.announcement.typeLabel}
            </span>
            {liveTypes.length === 0 && carriedRetiredType === null ? (
              <>
                <select className="field" disabled style={{ ...textFieldStyle, padding: '9px 30px 9px 11px' }}>
                  <option>{STR.modals.announcement.typeNoneYet}</option>
                </select>
                <span className="tx-subtle" style={hintStyle}>
                  {STR.modals.announcement.typeNoneYetHint}
                </span>
              </>
            ) : (
              <>
                <select
                  className="field"
                  value={typeId}
                  onChange={(event) => {
                    setTypeId(event.target.value);
                    setError(null);
                  }}
                  style={{ ...textFieldStyle, padding: '9px 30px 9px 11px' }}
                >
                  {/* Non-selectable — a type is required (D101). Without this the browser
                      would display the first real option while typeId still held '', which
                      would silently lie about what is about to be saved. */}
                  <option value="" disabled>
                    {STR.modals.announcement.typeChoose}
                  </option>
                  {liveTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                  {carriedRetiredType === null ? null : (
                    <option value={carriedRetiredType.id}>
                      {STR.applications.tile.retiredTagOption(carriedRetiredType.name)}
                    </option>
                  )}
                </select>
                <span className="tx-subtle" style={hintStyle}>
                  {STR.modals.announcement.typeHint}
                </span>
              </>
            )}
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ display: 'block' }}>
              <span
                className="tx-muted"
                style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'baseline', gap: 6 }}
              >
                {STR.modals.announcement.startsLabel}{' '}
                <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 500 }}>
                  {STR.settings.optional}
                </span>
              </span>
              <input
                className="field"
                type="datetime-local"
                value={starts}
                onChange={(event) => {
                  setStarts(event.target.value);
                  setError(null);
                }}
                style={{ ...textFieldStyle, fontSize: 12.5, padding: '8px 11px' }}
              />
            </label>
            <label style={{ display: 'block' }}>
              <span
                className="tx-muted"
                style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'baseline', gap: 6 }}
              >
                {STR.modals.announcement.endsLabel}{' '}
                <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 500 }}>
                  {STR.settings.optional}
                </span>
              </span>
              <input
                className="field"
                type="datetime-local"
                value={ends}
                onChange={(event) => {
                  setEnds(event.target.value);
                  setError(null);
                }}
                style={{ ...textFieldStyle, fontSize: 12.5, padding: '8px 11px' }}
              />
            </label>
          </div>
          <span className="tx-subtle" style={{ ...hintStyle, marginTop: -6 }}>
            {STR.modals.announcement.scheduleHint}
          </span>
        </div>

        <div style={{ minWidth: 0 }}>
          <div className="tx-subtle" style={{ ...eyebrowStyle, marginBottom: 8 }}>
            {STR.modals.announcement.previewLabel}
          </div>
          <div className="cardx ann">
            <div className="hd">
              <span className="t nm-tr">
                {previewTitle === ''
                  ? STR.modals.announcement.previewTitleFallback
                  : previewTitle}
              </span>
              <span className="d">{previewDate}</span>
            </div>
            {previewApplication === undefined && previewType === null ? null : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  flexWrap: 'wrap',
                  rowGap: 4,
                  marginTop: 6,
                }}
              >
                {previewApplication === undefined ? null : (
                  <Chip
                    tone="neutral"
                    swatch={previewApplication.pillColor}
                    style={{ fontSize: 10.5, padding: '2px 8px' }}
                  >
                    <span className="ptr">{previewApplication.name}</span>
                  </Chip>
                )}
                {previewType === null ? null : (
                  <Chip
                    tone="neutral"
                    swatch={previewType.color}
                    style={{ fontSize: 10.5, padding: '2px 8px' }}
                  >
                    <span className="ptr">{previewType.name}</span>
                  </Chip>
                )}
              </div>
            )}
            {body.trim() === '' ? (
              <p
                className="tx-subtle"
                style={{ margin: '6px 0 0', fontSize: 12.5, fontStyle: 'italic' }}
              >
                {STR.modals.announcement.previewBodyFallback}
              </p>
            ) : savedHtml === null ? (
              // NOT HTML. A React text node in the same whitespace-preserving container —
              // the only safe way to show an unsaved body, since the server has not rendered
              // it and this app has no renderer of its own.
              <div className="md">{body}</div>
            ) : (
              // THE SERVER'S OWN HTML, the only HTML this app ever injects.
              <div className="md" dangerouslySetInnerHTML={{ __html: savedHtml }} />
            )}
          </div>
          <span className="tx-subtle" style={{ ...hintStyle, marginTop: 10 }}>
            {STR.modals.announcement.previewNote}
          </span>
          {body.trim() !== '' && savedHtml === null ? (
            <span className="tx-subtle" style={{ ...hintStyle, marginTop: 4 }}>
              {STR.modals.announcement.previewUnrendered}
            </span>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

/**
 * New / edit announcement type. The colour comes from the ten-swatch token strip and
 * nowhere else — there is no free colour picker in this feature, so a hex value can never
 * reach the server from here (and would be refused if it did).
 */
function TypeFormModal({
  type,
  onClose,
}: {
  type: AnnouncementTypePublic | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(type?.name ?? '');
  // A NEW type opens on the first swatch in the contract's own order — never a literal, so
  // /web still spells no part of the vocabulary out.
  const [color, setColor] = useState<AnnouncementSwatch>(type?.color ?? ANNOUNCEMENT_SWATCHES[0]);
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: () => {
      const trimmed = name.trim();
      if (type === null) return createAnnouncementType({ name: trimmed, color });
      // A true patch: an unchanged field is not sent, so an idempotent save writes nothing
      // and records no audit row.
      return updateAnnouncementType(type.id, {
        ...(trimmed === type.name ? {} : { name: trimmed }),
        ...(color === type.color ? {} : { color }),
      });
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.announcementTypes });
      void queryClient.invalidateQueries({ queryKey: queryKeys.announcements });
      void queryClient.invalidateQueries({ queryKey: queryKeys.myAnnouncements });
      showToast(type === null ? STR.toast.typeCreated(result.type.name) : STR.toast.typeUpdated);
      onClose();
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  function submit() {
    if (name.trim() === '') {
      setError(STR.modals.announcementType.nameRequired);
      return;
    }
    saveMutation.mutate();
  }

  const sample = name.trim() === '' ? STR.modals.announcementType.previewFallback : name.trim();

  return (
    <Modal
      title={
        type === null ? STR.modals.announcementType.titleNew : STR.modals.announcementType.titleEdit
      }
      lead={STR.modals.announcementType.lead}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onClose}>
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            disabled={saveMutation.isPending}
            onClick={submit}
          >
            {type === null
              ? STR.modals.announcementType.submitNew
              : STR.modals.announcementType.submitEdit}
          </button>
        </>
      }
    >
      <ModalForm>
        {error === null ? null : (
          <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
            {error}
          </Callout>
        )}
        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.modals.announcementType.nameLabel}
          </span>
          <input
            className="field"
            type="text"
            maxLength={60}
            value={name}
            placeholder={STR.modals.announcementType.namePlaceholder}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
            style={textFieldStyle}
          />
        </label>
        <div>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.modals.announcementType.colourLabel}
          </span>
          <SwatchStrip
            value={color}
            label={STR.modals.announcementType.colourGroupLabel}
            onPick={(swatch) => {
              setColor(swatch);
              setError(null);
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <span className="tx-subtle" style={{ fontSize: 11.5 }}>
              {STR.modals.announcementType.previewLabel}
            </span>
            <Chip tone="neutral" swatch={color} style={{ fontSize: 10.5, padding: '2px 8px' }}>
              <span className="ptr">{sample}</span>
            </Chip>
          </div>
        </div>
      </ModalForm>
    </Modal>
  );
}
